import { create } from 'zustand';
import { normalizeBottomTabs } from '@shared/lib/bottomTabs';
import type {
  CompiledMemberAppExperience,
  CompiledNavigationItem,
  CompiledMoreTileCategory,
  CompiledBannerConfig,
} from '../types/domainConfig';

/**
 * `CompiledMemberAppExperience` state — drives the dynamic shell.
 * Backend ships per-tenant tabs, hamburger items, More-tile categories,
 * and banners; the React shell renders directly from this store rather
 * than from a hardcoded route table.
 */
const STORAGE_KEY = 'paas_app_experience';

export type ExperienceLoadState = 'idle' | 'loading' | 'ready' | 'error';

interface ExperienceContext {
  loadState?: ExperienceLoadState;
}

/**
 * Stable empty references. The `get*()` selectors below are consumed
 * directly as Zustand selectors (`useExperienceStore((s) => s.getBottomTabs())`);
 * returning a fresh `[]` each call makes `useSyncExternalStore` see a new
 * snapshot every render → "Maximum update depth exceeded". Returning a
 * shared constant keeps the snapshot referentially stable.
 */
const EMPTY_NAV: CompiledNavigationItem[] = [];
const EMPTY_CATEGORIES: CompiledMoreTileCategory[] = [];

function hasRenderableImageUrl(image: { image_url?: string | null }): boolean {
  return typeof image.image_url === 'string' && image.image_url.trim().length > 0;
}

function sanitizeBanners(experience: CompiledMemberAppExperience): CompiledMemberAppExperience {
  const banners = Object.entries(experience.banners ?? {}).reduce<CompiledMemberAppExperience['banners']>(
    (next, [key, banner]) => {
      if (!banner) return next;

      const images = (banner.images ?? []).filter(hasRenderableImageUrl);
      if (images.length > 0) {
        next[key] = { ...banner, images };
      }

      return next;
    },
    {},
  );

  return { ...experience, banners };
}

function withHomeFirst(experience: CompiledMemberAppExperience): CompiledMemberAppExperience {
  const sanitized = sanitizeBanners(experience);
  const bottom = sanitized.navigation?.bottom_tabs;
  if (!bottom?.length) return sanitized;
  return {
    ...sanitized,
    navigation: {
      ...sanitized.navigation!,
      bottom_tabs: normalizeBottomTabs(bottom),
    },
  };
}

interface ExperienceState {
  experience: CompiledMemberAppExperience | null;
  loadState: ExperienceLoadState;
  setExperience: (experience: CompiledMemberAppExperience | null, context?: ExperienceContext) => void;
  setLoadState: (loadState: ExperienceLoadState) => void;
  getBottomTabs: () => CompiledNavigationItem[];
  getHamburger: () => CompiledNavigationItem[];
  getMoreCategories: () => CompiledMoreTileCategory[];
  getBanner: (bannerKey: string) => CompiledBannerConfig | undefined;
  hydrate: () => void;
}

export const useExperienceStore = create<ExperienceState>((set, get) => ({
  experience: null,
  loadState: 'idle',

  setExperience(experience, context) {
    const next = experience ? withHomeFirst(experience) : null;
    set({ experience: next, loadState: context?.loadState ?? (next ? 'ready' : 'idle') });
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('[experience] cache clear failed:', error);
    }
  },

  setLoadState(loadState) {
    set({ loadState });
  },

  getBottomTabs() {
    return get().experience?.navigation?.bottom_tabs ?? EMPTY_NAV;
  },
  getHamburger() {
    return get().experience?.navigation?.hamburger ?? EMPTY_NAV;
  },
  getMoreCategories() {
    return get().experience?.tiles.more_categories ?? EMPTY_CATEGORIES;
  },
  getBanner(bannerKey) {
    return get().experience?.banners?.[bannerKey];
  },

  hydrate() {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('[experience] cache clear failed:', error);
    }
  },
}));
